import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Popover } from 'radix-ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useToast } from '@/hooks/useToast'
import { errorMessage } from '@/lib/apiError'
import { notificationHref, notificationKeys, notificationsApi, type AppNotification } from '@/api/notifications'
import { ErrorState } from '@/components/common/ErrorState'
import { Button } from '@/components/common/Button'
import { relativeTime } from '@/lib/relativeTime'

type Tab = 'unread' | 'all'

export function useUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: notificationsApi.unreadCount,
    enabled,
    // Spec 21 §3.6.2: poll every 60s and on window focus.
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

/** Mark-read that verifies the write landed before the UI claims it did. */
export function useNotificationActions() {
  const qc = useQueryClient()
  const toast = useToast()
  const refresh = () => qc.invalidateQueries({ queryKey: notificationKeys.all })

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const row = await notificationsApi.markRead(id)
      if (!row?.is_read) throw new Error('The notification could not be marked as read. Try again.')
      return row
    },
    onSettled: refresh,
    onError: (e) => toast.error(errorMessage(e), { title: "Couldn't mark as read" }),
  })

  const markAll = useMutation({
    mutationFn: async () => {
      const marked = await notificationsApi.markAllRead()
      const left = await notificationsApi.unreadCount()
      return { marked, left }
    },
    onSettled: refresh,
    onSuccess: ({ marked, left }) => {
      if (left > 0) toast.warning(`${left} notification${left === 1 ? ' is' : 's are'} still unread. Try again.`, { title: 'Not everything was marked' })
      else toast.success(marked === 1 ? '1 notification marked as read.' : `${marked} notifications marked as read.`)
    },
    onError: (e) => toast.error(errorMessage(e), { title: "Couldn't mark all as read" }),
  })

  return { markRead, markAll }
}

/**
 * One notification. It is a LINK (it navigates to the record), not a button: its body may
 * wrap to two lines, and read state is part of its accessible name rather than a hidden
 * span. Opening it marks it read.
 */
export function NotificationRow({ n, onOpen, compact }: { n: AppNotification; onOpen: (n: AppNotification) => void; compact?: boolean }) {
  return (
    <Link
      to={notificationHref(n)}
      onClick={() => onOpen(n)}
      aria-label={`${n.is_read ? '' : 'Unread: '}${n.title}${n.body ? ` — ${n.body}` : ''}`}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 text-left transition-colors duration-fast hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
        compact ? 'py-2.5' : 'py-3',
      )}
    >
      <span
        aria-hidden
        className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.is_read ? 'bg-transparent' : 'bg-primary')}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block break-words text-[13px]', n.is_read ? 'text-content-secondary' : 'font-semibold text-foreground')}>
          {n.title}
        </span>
        {n.body && (
          <span className="mt-0.5 line-clamp-2 block text-xs leading-5 text-muted-foreground" title={n.body}>{n.body}</span>
        )}
        <span className="mt-0.5 block text-[11px] text-muted-foreground">{relativeTime(n.created_at)}</span>
      </span>
    </Link>
  )
}

/**
 * Topbar bell + popover (spec 21 §3.6.2). Lists `GET /notifications` with Unread | All
 * tabs, mark-read on open, mark-all-read, and "See all" to the full page. Clicking an item
 * marks it read AND goes to its destination — every notification has one.
 */
export function NotificationCentre({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('unread')
  const { data: unread = 0 } = useUnreadCount(enabled)
  const { markRead, markAll } = useNotificationActions()

  const list = useQuery({
    queryKey: notificationKeys.list(tab, 10),
    queryFn: () => notificationsApi.list({ unreadOnly: tab === 'unread', perPage: 10 }),
    enabled: enabled && open,
    staleTime: 15_000,
    retry: false,
  })

  // The row is a Link, so navigation is the browser's; this marks it read and closes.
  const openItem = (n: AppNotification) => {
    if (!n.is_read) markRead.mutate(n.id)
    setOpen(false)
  }

  const badge = unread > 99 ? '99+' : String(unread)
  const items = list.data?.items ?? []

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative grid size-9 place-items-center rounded-md text-content-secondary transition-colors duration-fast hover:bg-surface-hover hover:text-foreground data-[state=open]:bg-surface-hover"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications, none unread'}
          title={unread > 0 ? `${unread} unread` : 'No new notifications'}
        >
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span className="absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-white ring-2 ring-card">
              {badge}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 flex max-h-[min(560px,calc(100dvh-80px))] w-[400px] max-w-[calc(100vw-24px)] flex-col rounded-lg border border-border bg-popover shadow-e3 outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-1"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
            <Button
              size="sm"
              variant="ghost"
              icon={<CheckCheck />}
              loading={markAll.isPending}
              disabled={unread === 0}
              onClick={() => markAll.mutate()}
            >
              Mark all read
            </Button>
          </div>
          <div role="tablist" aria-label="Filter notifications" className="flex gap-1 border-b border-border px-3">
            {(['unread', 'all'] as const).map((t) => (
              <button
                key={t}
                role="tab"
                type="button"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={cn(
                  '-mb-px border-b-2 px-2 py-2 text-[13px] font-medium transition-colors duration-fast',
                  tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t === 'unread' ? `Unread${unread ? ` (${badge})` : ''}` : 'All'}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {list.isLoading && (
              <div aria-busy="true" className="space-y-3 p-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="skeleton h-3 w-2/3" />
                    <div className="skeleton h-3 w-full" />
                  </div>
                ))}
              </div>
            )}
            {list.isError && (
              <ErrorState variant="inline" error={list.error} onRetry={() => list.refetch()} retrying={list.isFetching} title="Couldn't load notifications" />
            )}
            {list.isSuccess && items.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  {tab === 'unread' ? "You're all caught up" : 'No notifications yet'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tab === 'unread' ? 'New approvals, payslips and updates will appear here.' : 'Updates about your requests will appear here.'}
                </p>
              </div>
            )}
            {list.isSuccess && items.map((n) => <NotificationRow key={n.id} n={n} onOpen={openItem} compact />)}
          </div>
          <div className="border-t border-border px-4 py-2.5 text-right">
            <Link to="/notifications" onClick={() => setOpen(false)} className="text-[13px] font-medium text-link hover:underline">
              See all notifications
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
